/**
 * Handles the "nudge the business to rate Carata on the Play/App Store" flow.
 *
 * The business qualifies for the nudge once ALL of the following are true:
 *   - They've clicked "Start Preparing" on at least REQUIRED_START_PREPARING
 *     distinct orders (see Orders.js / mediators/Order.js#markAsProcessing).
 *   - The app has been opened at least REQUIRED_OPENS times (see boot.js).
 *   - At least REQUIRED_DAYS_SINCE_FIRST_OPEN days have passed since the
 *     app was first opened on this device.
 *
 * Once qualified (and not already responded), we show a small modal asking
 * them to rate the app. "Rate Now" triggers the native in-app review dialog
 * (Capacitor's `@capacitor-community/in-app-review`) and permanently
 * silences the nudge. "Maybe Later" snoozes it for REMIND_LATER_DAYS days.
 */
import { InAppReview } from "@capacitor-community/in-app-review";
import { app } from "modular-openscriptjs";
import { store, getStore, sendEvent, consoleErrorOnLocal } from "./helpers";

const STORAGE_KEY = "app_review_prompt_state";
const REQUIRED_START_PREPARING = 5;
const REQUIRED_OPENS = 5;
const REQUIRED_DAYS_SINCE_FIRST_OPEN = 5;
const REMIND_LATER_DAYS = 3;
const MAX_TRACKED_ORDER_IDS = 20;
const DAY_MS = 24 * 60 * 60 * 1000;

const defaultState = () => ({
    startPreparingOrderIds: [],
    startPreparingCount: 0,
    openCount: 0,
    firstOpenedAt: null,
    responded: false,
    laterUntil: null,
});

async function getState() {
    const state = await getStore(STORAGE_KEY);
    return { ...defaultState(), ...(state || {}) };
}

async function saveState(state) {
    await store(STORAGE_KEY, state);
}

function isNativePlatform() {
    return typeof Capacitor !== "undefined" && Capacitor.isNativePlatform();
}

/**
 * Call this once per app launch/foreground (see boot.js). Sets the
 * "first opened" timestamp the first time it's called, and increments the
 * open counter every time.
 */
export async function trackAppOpen() {
    if (!isNativePlatform()) return;

    const state = await getState();

    if (!state.firstOpenedAt) {
        state.firstOpenedAt = Date.now();
    }
    state.openCount += 1;

    await saveState(state);
}

/**
 * Call this whenever the business successfully clicks "Start Preparing" on
 * an order. Increments the (deduped) counter, then checks if it's time to
 * nudge them.
 * @param {number|string} orderId
 */
export async function trackStartPreparing(orderId) {
    if (!isNativePlatform() || !orderId) return;

    const state = await getState();

    if (!state.startPreparingOrderIds.includes(orderId)) {
        state.startPreparingOrderIds.push(orderId);
        // keep the tracked id list small, we only need it for de-duping
        if (state.startPreparingOrderIds.length > MAX_TRACKED_ORDER_IDS) {
            state.startPreparingOrderIds = state.startPreparingOrderIds.slice(
                -MAX_TRACKED_ORDER_IDS,
            );
        }
        state.startPreparingCount += 1;
        await saveState(state);
    }

    await maybeTriggerAppReviewPrompt(state);
}

/**
 * Checks eligibility and, if the business qualifies, broadcasts the event
 * that opens the review nudge modal.
 * @param {object} [preloadedState]
 */
export async function maybeTriggerAppReviewPrompt(preloadedState = null) {
    if (!isNativePlatform()) return;

    const state = preloadedState ?? (await getState());

    if (state.responded) return;
    if (state.startPreparingCount < REQUIRED_START_PREPARING) return;
    if (state.openCount < REQUIRED_OPENS) return;
    if (
        !state.firstOpenedAt ||
        Date.now() - state.firstOpenedAt < REQUIRED_DAYS_SINCE_FIRST_OPEN * DAY_MS
    )
        return;
    if (state.laterUntil && Date.now() < state.laterUntil) return;

    sendEvent(app("$e").appReview.needs.prompt);
}

/**
 * Called when the business taps "Rate Now" on the nudge modal.
 */
export async function acceptAppReviewPrompt() {
    const state = await getState();
    state.responded = true;
    await saveState(state);

    try {
        await InAppReview.requestReview();
    } catch (e) {
        consoleErrorOnLocal("InAppReview.requestReview failed", e);
    }
}

/**
 * Called when the business taps "Maybe Later" (or dismisses) the nudge modal.
 */
export async function snoozeAppReviewPrompt() {
    const state = await getState();
    state.laterUntil = Date.now() + REMIND_LATER_DAYS * DAY_MS;
    await saveState(state);
}
